/**
 * ADR-074 §3.5 — Email Validation surface API client (OUTBOUND sub-tab).
 * Thin typed wrapper over the shared `adminApi` fetch helper (mirrors
 * flow-validator-api.ts). The display state + effective stage are derived
 * server-side (the §3.3 honesty rule lives in the shared model), so the surface
 * just renders what it receives.
 */
import { adminApi, adminApiInZone } from './api-client';
import type { PortalZone } from './zones';

export type EmailStage =
  | 'Queued'
  | 'Sent'
  | 'Delivered'
  | 'Bounced'
  | 'Complaint'
  | 'Rejected'
  | 'InLimbo'
  | 'Read'
  | 'Closed';

export type DisplayTone = 'issue' | 'await' | 'agent' | 'ok';

export interface EmailTransition {
  stage: EmailStage;
  at: string;
  detail?: string;
  sesMessageId?: string;
  event?: 'resent';
}

export interface OutboundEmailRow {
  id: string;
  friendlyId: string;
  sesMessageId: string | null;
  recipient: string;
  fromAddress: string;
  flowType: string;
  workflow: string;
  subject: string;
  sentAt: string | null;
  lastEventAt: string;
  currentStage: EmailStage;
  effectiveStage: EmailStage;
  state: { label: string; tone: DisplayTone; isIssue: boolean };
  expectsResponse: boolean;
  resentCount: number | null;
  transitions: EmailTransition[];
  /** The zone this row came from — added by the client after the fan-out (no zone's API names
   *  itself). Absent on a row from a single-zone read. */
  zone?: string;
}

export interface OutboundEmailsResponse {
  emails: OutboundEmailRow[];
  count: number;
}

/**
 * GET /admin/email-validation/outbound — one zone's outbound journey list.
 *
 * Each zone sends its own email from its own SES and keeps its own EmailComm table, so the board
 * fans this out to every zone from the browser (`fanOutZones`, lib/zones.ts) and tags each row
 * with the zone it came from — read-only ops boards are zone-agnostic (admin-portal-ui-standards
 * § "Read-only ops boards are zone-agnostic", 13 October 2026).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ALL" — same convention as flow-validator-api.ts.
 */
export function getOutboundEmails(zone?: PortalZone): Promise<OutboundEmailsResponse> {
  const path = '/admin/email-validation/outbound';
  return zone ? adminApiInZone<OutboundEmailsResponse>(zone, path) : adminApi<OutboundEmailsResponse>(path);
}
